import { brandIcons } from '@/shared/brand-icons';
import { z } from 'zod';
import { accentColors } from '@/shared/color';
import { TenantPublic } from '@/shared/contracts';
import { DEFAULT_ACCENT, GROUND } from '@/shared/palette';
import { onThemeChange, type Theme } from './theme';

export const TenantBoot = TenantPublic.extend({
  app_env: z.string(),
  git_sha: z.string(),
  account_sessions: z.literal('accounts').optional(),
});
export type TenantBoot = z.infer<typeof TenantBoot>;
export const tenantQueryKey = ['tenant'] as const;
export const PUBLIC_REFRESH_MS = 60_000;

/** What the shell shows before the Worker has injected anything (the Vite dev server, a test). */
const FALLBACK: TenantBoot = {
  slug: 'academy',
  name: 'Expedition Certified',
  short_name: 'Certified',
  brand_mode: 'endorsed',
  accent_light: DEFAULT_ACCENT.light,
  accent_dark: DEFAULT_ACCENT.dark,
  logo_url: null,
  display_font: 'instrument-sans',
  credential_name: 'Expedition Cruise Specialist',
  operator_slug: null,
  enrolment_mode: 'invite',
  signatory_name: null,
  signatory_title: null,
  app_env: 'development',
  git_sha: 'dev',
};

/**
 * The tenant the Worker baked into `#ea-tenant`. An empty or malformed block (the Vite dev server
 * serves the raw template) falls back to the Academy so the app still boots.
 */
export function readTenant(): TenantBoot {
  const el = document.getElementById('ea-tenant');
  if (!el?.textContent?.trim()) return FALLBACK;
  try {
    const parsed = TenantBoot.safeParse(JSON.parse(el.textContent));
    return parsed.success ? parsed.data : FALLBACK;
  } catch {
    return FALLBACK;
  }
}

const THEME_COLOR: Record<Theme, string> = GROUND;

/** Paint the tenant's readable text, filled actions and matching hover state together. */
export function tenantStyle(t: TenantPublic, theme: Theme): Record<string, string> {
  const accent = theme === 'dark' ? t.accent_dark : t.accent_light;
  return {
    ...accentColors(accent, theme),
    // The stored value `instrument-sans` now means the default face, Hanken Grotesk.
    '--font-display': t.display_font === 'inter' ? 'Inter' : 'Hanken Grotesk',
  };
}

export function applyTenant(
  t: TenantPublic,
  // Another tab can change the shared cookie while this document still displays its own theme.
  theme: Theme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(tenantStyle(t, theme))) {
    root.style.setProperty(name, value);
  }
  if (t.display_font === 'inter') root.setAttribute('data-font', 'inter');
  else root.removeAttribute('data-font');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

/** Applies now and again on every theme change. Returns the unsubscribe. */
export function bindTenant(t: TenantPublic): () => void {
  applyTenantIcons(t);
  applyTenant(t);
  return onThemeChange((theme) => applyTenant(t, theme));
}

/** Public verifier pages use their issuing academy's icons, including on a different host. */
export function applyTenantIcons(t: TenantPublic): void {
  const icons = brandIcons(t);
  for (const [rel, src] of [
    ['icon', icons.favicon],
    ['apple-touch-icon', icons.apple],
  ] as const) {
    const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!src) existing?.remove();
    else {
      const link = existing ?? document.createElement('link');
      link.rel = rel;
      link.href = src;
      link.type = /\.svg(\?|$)/.test(src) ? 'image/svg+xml' : 'image/png';
      if (!existing) document.head.appendChild(link);
    }
  }
}
